/**
 * Phase 3 PR #4 sub-phase 3H (2026-05-18) — Workers comp + payroll tax
 * employer-oncosts calculator.
 *
 * Pure functional module. NO I/O, NO async, NO database access. Inputs are
 * already-resolved primitives + already-computed wage figures; output is a
 * snapshot JSONB shape persisted on `pay_run_items.employer_oncosts_snapshot`.
 *
 * NOT an award-engine math change
 * --------------------------------
 * This module sits ALONGSIDE the four award engines (SCHADS / Aged Care /
 * Nurses / HPSS). It consumes their `breakdown.grossTotal` output but does
 * NOT modify employee-paid wage math. The four engine versions stay at
 * SCHADS 5.6.1 / Aged Care 5.6.1 / Nurses 5.6.3 / HPSS 5.6.2 — this PR
 * carries the `[engine-no-math-change]` tag.
 *
 * The 8th property-based byte-equal invariant (replay vs live snapshot of
 * pay_run_items.breakdown_snapshot) is preserved because this module writes
 * to a SIBLING column (`employer_oncosts_snapshot`) and never touches the
 * existing `breakdown_snapshot` JSONB shape.
 *
 * Components
 * ----------
 * A) Workers comp insurance premium
 *    - Per state × industry classification (stream + level) × FY-effective
 *      rate, looked up from `workers_comp_rates`.
 *    - Calculation: premiumAud = round2(itemGrossWages × ratePercent / 100).
 *    - Each state has its own scheme (VIC WorkSafe / NSW icare / etc).
 *    - Default fallback: if no rate row matches, premium = 0 + diagnostic flag.
 *
 * B) Payroll tax
 *    - Per state × FY threshold + rate, looked up from `payroll_tax_thresholds`.
 *    - Calculation: only wages ABOVE the (state, FY) threshold attract tax.
 *      Threshold is annual; the calculator threads YTD-cumulative-wages so
 *      each pay-run item gets a marginal slice.
 *
 *      For a single item adding `itemGrossWages` to (state, FY) where the
 *      pre-item YTD cumulative is `ytdBefore`:
 *
 *        ytdAfter = ytdBefore + itemGrossWages
 *        if ytdBefore >= threshold:
 *          taxableThisItem = itemGrossWages
 *        elif ytdAfter > threshold:
 *          taxableThisItem = ytdAfter - threshold
 *        else:
 *          taxableThisItem = 0
 *        thisItemAud = round2(taxableThisItem × ratePercent / 100)
 *
 *    - Default fallback: if no threshold row matches, tax = 0 + diagnostic flag.
 *
 * Determinism + idempotency
 * -------------------------
 * - Pure function over (item wages, state, FY, rate row, threshold row,
 *   YTD-before). Same inputs → same output.
 * - All money values rounded to 2 decimal places (cents-accurate) before
 *   persistence.
 * - Calculator version pinned in the snapshot so we can detect output-shape
 *   changes during 18-month replay.
 */

/**
 * Calculator version. Bumps:
 *   3h.1.0 — initial 3H ship (Phase 3 PR#4, 2026-05-18). Exact-match lookup
 *            only; missing rates → 0 + diagnostic flag.
 *   3h.2.0 — G12 + G29 wildcard fallback (2026-05-18). Lookup order:
 *              1. exact (state, stream, level) match
 *              2. wildcard (state, '*', '*') platform-default match
 *              3. unresolved → 0 + diagnostic flag
 *            Payroll tax unchanged in wildcard shape (no stream/level dim);
 *            calculator-version bumps for both so the snapshot semantics are
 *            atomic — every employer_oncosts_snapshot stamps 3h.2.0 carries
 *            "fallback was tried before unresolved" guarantee.
 *   3h.2.1 — Codex P2-A + P2-B (2026-05-18).
 *            P2-A: a matched row whose `source_citation` contains the
 *                  `[NEEDS_PRIMARY_SOURCE]` tag (or whose `is_placeholder`
 *                  hint is true) is RESOLVED — premium is correctly $0 —
 *                  but a NEW diagnostic `rateNeedsPrimarySource: true` is
 *                  emitted so UI surfaces an amber chip and pay-run-readiness
 *                  blocks STP/Xero export until a tenant override (or a
 *                  validated platform replacement) is in place.
 *            P2-B: rate-row selection is now per-item-date strict. Callers
 *                  pass `shiftDate` (the per-item shift calendar date) and
 *                  the calculator filters rows with
 *                      effective_from <= shiftDate
 *                      AND (effective_to IS NULL OR effective_to >= shiftDate)
 *                  Deterministic tiebreaker: row with the LATEST
 *                  `effective_from` wins (most recent applicable seed). This
 *                  closes the boundary-straddling defect where a pay period
 *                  spanning a rate-effective boundary could return the wrong
 *                  overlapping row from a pre-filtered prefetch.
 */
export const EMPLOYER_ONCOSTS_CALCULATOR_VERSION = '3h.2.1' as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Supported state codes (matches workers_comp_rates + payroll_tax_thresholds CHECK). */
export type EmployerOncostsState = 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT';

const VALID_STATES: ReadonlySet<EmployerOncostsState> = new Set([
  'NSW',
  'VIC',
  'QLD',
  'WA',
  'SA',
  'TAS',
  'ACT',
  'NT',
]);

export function isEmployerOncostsState(value: unknown): value is EmployerOncostsState {
  return typeof value === 'string' && VALID_STATES.has(value as EmployerOncostsState);
}

/** Workers comp rate row (subset). */
export interface WorkersCompRateRow {
  id: string;
  state: string;
  classification_stream: string;
  classification_level: string;
  rate_percent: number | string; // numeric → may come back as string from Supabase
  source_citation: string | null;
  /**
   * Calculator 3h.2.1 (Codex P2-B, 2026-05-18) — strict per-item date filter.
   * `effective_from` is required for date-range filtering; `effective_to` is
   * nullable (open-ended row). Callers SHOULD include these columns in their
   * SELECT — calculator falls back to "row always applies" (back-compat) when
   * both fields are undefined to keep existing fixtures green.
   */
  effective_from?: string | null;
  effective_to?: string | null;
  /**
   * Calculator 3h.2.1 (Codex P2-A, 2026-05-18) — explicit placeholder hint.
   * Optional column added by migration `20267091000000`. When true, the row
   * is a known-not-validated stub (rate may be 0%) and the calculator emits
   * `rateNeedsPrimarySource=true`. For back-compat when the column is absent,
   * the calculator also detects `source_citation LIKE '%[NEEDS_PRIMARY_SOURCE]%'`.
   */
  is_placeholder?: boolean | null;
}

/** Payroll tax threshold row (subset). */
export interface PayrollTaxThresholdRow {
  id: string;
  state: string;
  fy_year: string;
  threshold_annual_aud: number | string;
  rate_percent: number | string;
  source_citation: string | null;
  /**
   * Calculator 3h.2.1 (Codex P2-A, 2026-05-18) — placeholder hint, same
   * semantics as the workers_comp_rates column.
   */
  is_placeholder?: boolean | null;
}

/** Snapshot — workers comp component. */
export interface WorkersCompSnapshot {
  premiumAud: number;
  stateCode: EmployerOncostsState | null;
  classificationStream: string | null;
  classificationLevel: string | null;
  ratePercent: number;
  sourceCitation: string | null;
  ratesRowId: string | null;
  /** Diagnostic — true when no matching workers_comp_rates row found (after wildcard fallback); premium = 0. */
  rateUnresolved: boolean;
  /**
   * G12 (2026-05-18) — diagnostic. True when the resolved row was the
   * platform wildcard (state, '*', '*') row rather than an exact tenant
   * (state, stream, level) match. UI surfaces this to let admins know they
   * are running on the platform default and can override.
   */
  rateWildcardUsed: boolean;
  /**
   * Codex P2-A (2026-05-18) — diagnostic. True when the resolved row is a
   * known-not-yet-validated placeholder seed (detected via either
   * `is_placeholder=true` column OR a `source_citation` containing the
   * `[NEEDS_PRIMARY_SOURCE]` tag — both seeded by migration
   * `20267090000000_g12_g29_employer_oncosts_platform_defaults.sql` for the
   * 4 states whose state-authority PDF was unreachable within the Q3
   * research budget). The row is RESOLVED (no `rateUnresolved=true`) and
   * the premium is the correct $0, but admin UI MUST surface this distinct
   * from a "row not present" case, and pay-run-readiness MUST block
   * STP/Xero export until the placeholder is overridden by a tenant or
   * replaced by a validated platform-default seed.
   */
  rateNeedsPrimarySource: boolean;
}

/** Snapshot — payroll tax component. */
export interface PayrollTaxSnapshot {
  thisItemAud: number;
  cumulativeWagesYtdBefore: number;
  cumulativeWagesYtdAfter: number;
  thresholdAud: number;
  ratePercent: number;
  stateCode: EmployerOncostsState | null;
  fyYear: string | null;
  thresholdReached: boolean;
  sourceCitation: string | null;
  thresholdsRowId: string | null;
  /** Diagnostic — true when no matching payroll_tax_thresholds row found; tax = 0. */
  thresholdUnresolved: boolean;
  /**
   * Codex P2-A (2026-05-18) — diagnostic, same semantics as
   * `workersComp.rateNeedsPrimarySource`. True when the resolved threshold
   * row carries `is_placeholder=true` or a `[NEEDS_PRIMARY_SOURCE]` citation.
   * All 8 FY26 payroll-tax rows in `20267090000000_*.sql` are primary-source
   * verified, so this flag is expected to stay `false` in current production
   * — it exists for parity with the workers-comp diagnostic and to support
   * future FY rolls (e.g. FY27 placeholder rows before AWR-2027 confirmation).
   */
  thresholdNeedsPrimarySource: boolean;
}

/** Top-level snapshot shape persisted to pay_run_items.employer_oncosts_snapshot. */
export interface EmployerOncostsSnapshot {
  workersComp: WorkersCompSnapshot;
  payrollTax: PayrollTaxSnapshot;
  calculatorVersion: typeof EMPLOYER_ONCOSTS_CALCULATOR_VERSION;
}

/** Input to the calculator. */
export interface EmployerOncostsInput {
  /** Gross wages for this single pay_run_item (employee-paid total). */
  itemGrossWagesAud: number;
  /** Resolved facility state for the underlying shift (null when unknown). */
  state: EmployerOncostsState | null;
  /** Classification stream/level used to pick a workers-comp rate row. */
  classificationStream: string | null;
  classificationLevel: string | null;
  /**
   * Calculator 3h.2.1 (Codex P2-B, 2026-05-18) — per-item shift calendar
   * date (`YYYY-MM-DD`) used to filter the prefetched workers-comp rate
   * rows by `effective_from <= shiftDate AND (effective_to IS NULL OR
   * effective_to >= shiftDate)`. Required so a prefetch that overlaps the
   * pay period doesn't return a row from the wrong side of a rate boundary
   * when the period straddles 1 Jul (FY transition is the most common
   * boundary, but verified rate seeds use other boundary dates too).
   *
   * Optional for back-compat: when undefined, the calculator falls through
   * to "row matches" without date filtering (the prior 3h.2.0 behaviour).
   * Date filtering also short-circuits if a row has neither `effective_from`
   * nor `effective_to` populated (test fixtures default to no dates).
   */
  shiftDate?: string;
  /**
   * Financial year label, e.g. 'FY26'. Computed from the shift date via
   * `financialYearLabel()` by the caller.
   */
  fyYear: string;
  /**
   * Tenant-wide YTD cumulative wages for (state, fyYear) BEFORE this item is
   * added. Caller is responsible for accumulating this across the snapshot
   * loop (each new item's snapshot reflects the running tally).
   */
  ytdCumulativeWagesAud: number;
  /**
   * Pre-fetched workers_comp_rates rows for the tenant (single state filter
   * happens inside the calculator). May be empty.
   */
  workersCompRates: WorkersCompRateRow[];
  /**
   * Pre-fetched payroll_tax_thresholds rows for the tenant (single state +
   * fyYear filter happens inside the calculator). May be empty.
   */
  payrollTaxThresholds: PayrollTaxThresholdRow[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Australian financial year label from a Date. Australia's FY runs 1 Jul
 * year-N to 30 Jun year-(N+1). The FY is labelled by the end year, last two
 * digits: e.g. 2025-07-01 → 'FY26'.
 */
export function financialYearLabel(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  // Use UTC components to keep this pure; caller has already resolved tz to
  // the shift's local date via existing tz helpers.
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth(); // 0-indexed; 6 = July
  const fyEndYear = month >= 6 ? year + 1 : year;
  const twoDigits = String(fyEndYear).slice(-2).padStart(2, '0');
  return `FY${twoDigits}`;
}

function coerceNumeric(value: number | string): number {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

// ---------------------------------------------------------------------------
// Workers comp
// ---------------------------------------------------------------------------

/** Wildcard marker used in workers_comp_rates platform-default rows. */
const WORKERS_COMP_WILDCARD = '*' as const;

/** Codex P2-A — citation tag used by migration `20267090000000_*.sql` for
 *  unverified-rate platform-default rows. */
const NEEDS_PRIMARY_SOURCE_TAG = '[NEEDS_PRIMARY_SOURCE]' as const;

type WorkersCompPick =
  | { row: WorkersCompRateRow; wildcardUsed: false }
  | { row: WorkersCompRateRow; wildcardUsed: true }
  | null;

/**
 * Codex P2-B (2026-05-18) — strict per-item date filter for `effective_from`/
 * `effective_to` overlapping the shift date. When neither column is populated
 * on a row (test fixtures often omit these), the row is treated as
 * always-applicable for back-compat. When `shiftDate` itself is undefined,
 * filtering is skipped entirely (caller opted out of date strictness — eg.
 * legacy non-date-aware call sites).
 */
function rowAppliesOnDate(row: WorkersCompRateRow, shiftDate: string | undefined): boolean {
  if (!shiftDate) return true;
  const from = row.effective_from ?? null;
  const to = row.effective_to ?? null;
  if (from === null && to === null) return true;
  if (from !== null && shiftDate < from) return false;
  if (to !== null && shiftDate > to) return false;
  return true;
}

/**
 * Codex P2-B (2026-05-18) — deterministic tiebreaker. When multiple rows
 * overlap the same `shiftDate`, the row with the LATEST `effective_from`
 * wins (most recent seed). `effective_from` is sortable ISO `YYYY-MM-DD`.
 * Rows missing the column sort last (treated as oldest).
 */
function pickLatestEffectiveFrom(candidates: WorkersCompRateRow[]): WorkersCompRateRow | undefined {
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];
  // Sort descending by effective_from (null → ''). Stable sort preserves
  // caller-provided ordering (tenant-first) on ties.
  return [...candidates].sort((a, b) => {
    const af = a.effective_from ?? '';
    const bf = b.effective_from ?? '';
    if (af === bf) return 0;
    return af < bf ? 1 : -1;
  })[0];
}

/**
 * G12 (2026-05-18) — wildcard fallback. Pick order:
 *   1. exact (state, classificationStream, classificationLevel) row
 *   2. wildcard (state, '*', '*') platform-default row (calculator-side
 *      caller passes platform-default rows alongside tenant rows; the lookup
 *      doesn't need to know which is which — it just prefers exact match
 *      over wildcard)
 *   3. null → caller emits rateUnresolved
 *
 * Caller-unchanged contract: callers continue to pass the union of tenant
 * rows + platform-default rows in `workersCompRates`. Calculator picks
 * deterministically without needing extra inputs.
 *
 * Codex P2-B (2026-05-18) — rows are filtered by `shiftDate` overlap
 * (`effective_from <= shiftDate AND (effective_to IS NULL OR
 * effective_to >= shiftDate)`) before either bucket is searched. When
 * multiple rows in the same bucket overlap, the latest `effective_from`
 * wins.
 */
function pickWorkersCompRate(
  rows: WorkersCompRateRow[],
  state: EmployerOncostsState | null,
  classificationStream: string | null,
  classificationLevel: string | null,
  shiftDate: string | undefined,
): WorkersCompPick {
  if (!state) return null;

  const applicable = rows.filter((r) => r.state === state && rowAppliesOnDate(r, shiftDate));

  // 1. Exact match wins (tenant-specific or platform-default exact). On ties
  //    (multiple overlapping seeds for the same exact key) prefer the row
  //    with the latest `effective_from`.
  if (classificationStream && classificationLevel) {
    const exactCandidates = applicable.filter(
      (r) =>
        r.classification_stream === classificationStream &&
        r.classification_level === classificationLevel,
    );
    const exact = pickLatestEffectiveFrom(exactCandidates);
    if (exact) return { row: exact, wildcardUsed: false };
  }

  // 2. Wildcard (state, '*', '*') fallback with latest-effective-from
  //    tiebreaker.
  const wildcardCandidates = applicable.filter(
    (r) =>
      r.classification_stream === WORKERS_COMP_WILDCARD &&
      r.classification_level === WORKERS_COMP_WILDCARD,
  );
  const wildcard = pickLatestEffectiveFrom(wildcardCandidates);
  if (wildcard) return { row: wildcard, wildcardUsed: true };

  return null;
}

/**
 * Codex P2-A (2026-05-18) — detect "this row is a known-not-validated
 * placeholder". Used by both the workers-comp and payroll-tax calculators.
 * Two signals: explicit `is_placeholder` boolean column, OR the
 * `[NEEDS_PRIMARY_SOURCE]` tag in `source_citation` (used by the existing
 * G12+G29 seed migration `20267090000000_*.sql`).
 */
function rowNeedsPrimarySource(row: {
  source_citation?: string | null;
  is_placeholder?: boolean | null;
}): boolean {
  if (row.is_placeholder === true) return true;
  const cite = row.source_citation ?? '';
  return cite.indexOf(NEEDS_PRIMARY_SOURCE_TAG) !== -1;
}

function calculateWorkersComp(
  itemGrossWagesAud: number,
  state: EmployerOncostsState | null,
  classificationStream: string | null,
  classificationLevel: string | null,
  rates: WorkersCompRateRow[],
  shiftDate: string | undefined,
): WorkersCompSnapshot {
  const pick = pickWorkersCompRate(
    rates,
    state,
    classificationStream,
    classificationLevel,
    shiftDate,
  );
  if (!pick) {
    return {
      premiumAud: 0,
      stateCode: state,
      classificationStream,
      classificationLevel,
      ratePercent: 0,
      sourceCitation: null,
      ratesRowId: null,
      rateUnresolved: true,
      rateWildcardUsed: false,
      rateNeedsPrimarySource: false,
    };
  }
  const ratePercent = coerceNumeric(pick.row.rate_percent);
  // eslint-disable-next-line statura/no-floating-point-money -- rate * wages is a percentage calculation; round2() snaps to cents on the final figure (calculator semantics; pinned by employer-oncosts-3h.test.ts cent-accurate toBe)
  const premiumAud = round2((itemGrossWagesAud * ratePercent) / 100);
  return {
    premiumAud,
    stateCode: state,
    classificationStream,
    classificationLevel,
    ratePercent,
    sourceCitation: pick.row.source_citation,
    ratesRowId: pick.row.id,
    rateUnresolved: false,
    rateWildcardUsed: pick.wildcardUsed,
    rateNeedsPrimarySource: rowNeedsPrimarySource(pick.row),
  };
}

// ---------------------------------------------------------------------------
// Payroll tax
// ---------------------------------------------------------------------------

function pickPayrollTaxThreshold(
  rows: PayrollTaxThresholdRow[],
  state: EmployerOncostsState | null,
  fyYear: string,
): PayrollTaxThresholdRow | null {
  if (!state) return null;
  return rows.find((r) => r.state === state && r.fy_year === fyYear) ?? null;
}

function calculatePayrollTax(
  itemGrossWagesAud: number,
  state: EmployerOncostsState | null,
  fyYear: string,
  ytdBefore: number,
  thresholds: PayrollTaxThresholdRow[],
): PayrollTaxSnapshot {
  const matched = pickPayrollTaxThreshold(thresholds, state, fyYear);
  if (!matched) {
    return {
      thisItemAud: 0,
      cumulativeWagesYtdBefore: round2(ytdBefore),
      cumulativeWagesYtdAfter: round2(ytdBefore + itemGrossWagesAud),
      thresholdAud: 0,
      ratePercent: 0,
      stateCode: state,
      fyYear: state ? fyYear : null,
      thresholdReached: false,
      sourceCitation: null,
      thresholdsRowId: null,
      thresholdUnresolved: true,
      thresholdNeedsPrimarySource: false,
    };
  }

  const thresholdAud = coerceNumeric(matched.threshold_annual_aud);
  const ratePercent = coerceNumeric(matched.rate_percent);
  const ytdAfter = ytdBefore + itemGrossWagesAud;

  let taxableThisItem = 0;
  if (ytdBefore >= thresholdAud) {
    taxableThisItem = itemGrossWagesAud;
  } else if (ytdAfter > thresholdAud) {
    taxableThisItem = ytdAfter - thresholdAud;
  } else {
    taxableThisItem = 0;
  }

  const thisItemAud = round2((taxableThisItem * ratePercent) / 100);
  return {
    thisItemAud,
    cumulativeWagesYtdBefore: round2(ytdBefore),
    cumulativeWagesYtdAfter: round2(ytdAfter),
    thresholdAud,
    ratePercent,
    stateCode: state,
    fyYear,
    thresholdReached: ytdAfter > thresholdAud,
    sourceCitation: matched.source_citation,
    thresholdsRowId: matched.id,
    thresholdUnresolved: false,
    thresholdNeedsPrimarySource: rowNeedsPrimarySource(matched),
  };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Top-level pure calculator. Returns the snapshot JSONB shape ready for
 * persistence on `pay_run_items.employer_oncosts_snapshot`.
 *
 * Per cost-tooltip persistence rule (`feedback_paired_breakdown_persistence_and_tooltip.md`),
 * every cost figure surfaced anywhere in the UI must be backed by a JSONB
 * snapshot persisted on the regulated wage row. This calculator + the
 * caller wiring in `pay-run-actions.ts:snapshotPayRun` is that paired ship.
 */
export function calculateEmployerOncosts(input: EmployerOncostsInput): EmployerOncostsSnapshot {
  const stateRaw = input.state;
  const state: EmployerOncostsState | null = isEmployerOncostsState(stateRaw) ? stateRaw : null;

  const workersComp = calculateWorkersComp(
    input.itemGrossWagesAud,
    state,
    input.classificationStream,
    input.classificationLevel,
    input.workersCompRates,
    input.shiftDate,
  );

  const payrollTax = calculatePayrollTax(
    input.itemGrossWagesAud,
    state,
    input.fyYear,
    input.ytdCumulativeWagesAud,
    input.payrollTaxThresholds,
  );

  return {
    workersComp,
    payrollTax,
    calculatorVersion: EMPLOYER_ONCOSTS_CALCULATOR_VERSION,
  };
}
